import { Link } from 'react-router-dom'

import murmurationLogo from '../assets/murmuration-logo.png'
import murmurationSymbol from '../assets/murmuration-symbol.png'

/**
 * Murmuration brand mark.
 *
 * Two assets back three variants:
 *   `murmuration-logo.png`    the full lockup - chain-bird flock over the
 *                             MURMURATION wordmark.
 *   `murmuration-symbol.png`  the lead chain-bird alone, for sizes where the
 *                             wordmark would be illegible.
 *
 * Both are supplied on the brand navy (`#0F1F34`), which is also `--side`, so
 * in the sidebar the lockup blends into the surface and reads as a bare logo.
 * On light surfaces the artwork needs a ground of its own, so `splash` and
 * `compact` set the same navy behind it as a rounded panel rather than
 * floating a navy square on the page background.
 *
 * | variant   | asset  | used on                                  |
 * | --------- | ------ | ---------------------------------------- |
 * | `sidebar` | lockup | the signed-in shell rail (links home)    |
 * | `splash`  | lockup | /sign-in, as the page heading            |
 * | `compact` | symbol | minimal chrome on /access-denied         |
 *
 * Sizing and the negative margins on the sidebar variant come from the design
 * handoff: the lockup has generous internal whitespace, so it is pulled in on
 * three sides to optically align with the nav items below it.
 *
 * Accessibility: where the mark is a link it carries the accessible name via
 * `aria-label` and the image's `alt` is empty, so a screen reader announces the
 * destination once rather than reading logo and link separately. RTL
 * `getByRole('link', { name: /murmuration/i })` resolves through the label.
 */
export interface BrandProps {
  readonly to?: string
  readonly variant?: 'sidebar' | 'splash' | 'compact'
}

export function Brand({ to, variant = 'sidebar' }: BrandProps): JSX.Element {
  if (variant === 'splash') {
    return (
      <img
        src={murmurationLogo}
        alt="Murmuration"
        decoding="async"
        className="mx-auto block h-32 w-32 rounded-md bg-side"
      />
    )
  }

  if (variant === 'compact') {
    const image = (
      <img
        src={murmurationSymbol}
        alt={to === undefined ? 'Murmuration' : ''}
        decoding="async"
        className="block h-8 w-8 rounded-sm bg-side"
      />
    )
    if (to === undefined) return image
    return (
      <Link to={to} aria-label="Murmuration" className="inline-flex no-underline">
        {image}
      </Link>
    )
  }

  return (
    <Link
      to={to ?? '/members'}
      aria-label="Murmuration - back to members"
      className="block select-none no-underline hover:no-underline"
    >
      <img
        src={murmurationLogo}
        alt=""
        decoding="async"
        className="-mb-[18px] -ml-[10px] -mt-[14px] block h-[130px] w-[130px]"
      />
    </Link>
  )
}
